// Eszköz create/edit — jobb oldali beúszó FormSheet (a meetings/service-catalog szerkesztő-mintája).
// Egy komponens KÉT módban: `asset == null` → LÉTREHOZÁS (POST), különben SZERKESZTÉS (PATCH If-Match/OCC).
// A validálás forrás-igazsága a @atlas/contracts AssetWriteBodySchema (a resolver a features/assets/form.ts
// segédein át futtatja) + az üzleti szabály: status=retired → retirementDate KÖTELEZŐ (a szerver is 422).
// Az assetNumber GENERÁLT (nem form-mező), a category a type-ból származik a BFF-en (csak type-választó van).
// A netPrice/vatRate STRING marad (Decimal — nem konvertáljuk number-re). A „KINEK" (Ilcsa #114) egy munkatárs
// (belső címtár KÖZÖS keresője) VAGY az iroda (általános használat) — a csapatot a szerver számolja a
// birtokos beosztásából, nincs választó. A típus-opciók a szerkeszthető KATALÓGUSBÓL jönnek (kategóriánként
// csoportosítva). Szoftver-típusnál a LEJÁRATI dátum is megadható (előfizetés, licenc) — erre megy a
// napi lejárat-értesítő.
import { type AssetRowSchema } from '@atlas/contracts';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { AssetRecipientFields } from '@/components/assets/asset-recipient-fields';
import { DateField, SelectField, TextField } from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Text } from '@/components/ui/text';
import { assetKeys } from '@/features/assets/api';
import { catalogText, useAssetCatalog } from '@/features/assets/catalog';
import {
  buildAssetResolver,
  EMPTY_ASSET_FORM,
  isManualStatus,
  MANUAL_STATUS_OPTIONS,
  NONE,
  OFFICE_USAGE,
  toAssetBody,
  toDateInput,
  type AssetFormValues,
} from '@/features/assets/form';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';

type AssetRow = z.infer<typeof AssetRowSchema>;

export interface AssetFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → LÉTREHOZÁS; egyébként a szerkesztendő eszköz (hordozza az OCC `version`-t + az előtöltést). */
  asset: AssetRow | null;
}

export function AssetFormSheet({ open, onOpenChange, asset }: AssetFormSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isEdit = asset !== null;

  // A típus-opciók és a szoftver-jelleg a szerkeszthető katalógusból (jog: asset_inventory.view).
  const { lookup } = useAssetCatalog();

  const form = useForm<AssetFormValues>({
    resolver: buildAssetResolver(t, lookup.isSoftwareType),
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: EMPTY_ASSET_FORM,
  });
  const { reset, handleSubmit, setError, setValue, watch } = form;
  // retired státusznál a selejtezés dátuma megjelenik és kötelező; nem-retired-nél rejtett (submitkor null).
  const status = watch('status');
  // a lejárati dátum CSAK szoftver-típusnál értelmezett (előfizetés, licenc) — hardvernél rejtett
  const type = watch('type');

  useEffect(() => {
    if (asset) {
      reset({
        name: asset.name,
        type: asset.type,
        // Nem-kézi státusz (pl. `received`): a mező ÜRESEN indul — a mentéshez választani KELL a
        // 4 kézi státuszból (legacy-parity: a beérkezett eszközt kézzel állítják tovább); néma
        // átírás (received→purchased) nincs.
        status: isManualStatus(asset.status) ? asset.status : '',
        serialNumber: asset.serialNumber ?? '',
        licenseKey: asset.licenseKey ?? '',
        netPrice: asset.netPrice ?? '',
        vatRate: asset.vatRate ?? '',
        purchaseDate: toDateInput(asset.purchaseDate),
        retirementDate: toDateInput(asset.retirementDate),
        expiryDate: toDateInput(asset.expiryDate),
        purchaseLocation: asset.purchaseLocation ?? '',
        invoiceNumber: asset.invoiceNumber ?? '',
        officeUse: asset.officeUse,
        holderIdentityId: asset.holderId ?? '',
        // személynél a `social` érvénytelen hely (a szerver elutasítaná) → „nincs megadva", nem néma elfogadás
        usageLocation:
          !asset.officeUse && asset.usageLocation === OFFICE_USAGE
            ? NONE
            : (asset.usageLocation ?? NONE),
      });
    } else {
      reset(EMPTY_ASSET_FORM);
    }
  }, [asset, reset]);

  // Új eszköznél a típus az első katalógus-típusra áll, amint a katalógus betöltött (különben üres és kötelező).
  const firstTypeKey = lookup.categories.find((c) => c.types.length > 0)?.types[0]?.key;
  useEffect(() => {
    if (!isEdit && type === '' && firstTypeKey) setValue('type', firstTypeKey);
  }, [isEdit, type, firstTypeKey, setValue]);

  const mutation = useEntityMutation<AssetFormValues>({
    mutationFn: (values) => {
      const body = toAssetBody(values, lookup.isSoftwareType(values.type));
      return isEdit
        ? api.assetUpdate.mutation({
            params: { id: asset.id },
            body,
            headers: { 'if-match': String(asset.version) },
          })
        : api.assetCreate.mutation({ body });
    },
    invalidateKeys: [assetKeys.lists()],
    onFieldErrors: (field, messages) => {
      if (field in EMPTY_ASSET_FORM) {
        setError(field as keyof AssetFormValues, { message: messages.join(' ') });
      }
    },
    messages: {
      success: isEdit ? t('toast:assets.updated') : t('toast:assets.created'),
      conflict: t('toast:assets.conflict'),
      forbidden: t('toast:assets.forbidden'),
      validation: t('toast:assets.validation'),
      error: t('toast:assets.error'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, { onSuccess: () => onOpenChange(false) });
  });

  // Típus-opciók a katalógusból, kategóriánként csoportosítva (a kategória a csoport-címke).
  const typeGroups = lookup.categories
    .filter((c) => c.types.length > 0)
    .map((c) => ({
      label: catalogText(c.name, lang),
      options: c.types.map((ty) => ({ value: ty.key, label: catalogText(ty.name, lang) })),
    }));
  // Státusz: CSAK a kézzel választható halmaz (a workflow-státuszok a 2. increment).
  const statusOptions = MANUAL_STATUS_OPTIONS.map((v) => ({
    value: v,
    label: t(`assets.statusLabel.${v}`, { defaultValue: v }),
  }));

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset(EMPTY_ASSET_FORM);
        onOpenChange(o);
      }}
      title={isEdit ? t('assets.editTitle') : t('assets.createTitle')}
      description={isEdit ? asset.name : t('assets.createDesc')}
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('assets.cancel')}
          </Button>
          <Button type="submit" variant="brand" disabled={mutation.isPending}>
            {mutation.isPending ? t('assets.saving') : t('assets.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        <TextField<AssetFormValues>
          name="name"
          label={t('assets.fields.name')}
          required
          placeholder={t('assets.fields.namePlaceholder')}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField<AssetFormValues>
            name="type"
            label={t('assets.fields.type')}
            required
            groups={typeGroups}
          />
          <SelectField<AssetFormValues>
            name="status"
            label={t('assets.fields.status')}
            required
            options={statusOptions}
          />
        </div>
        {/* Selejtezés dátuma — CSAK retired státusznál (ott kötelező; a szerver is 422-vel kényszeríti). */}
        {status === 'retired' && (
          <DateField<AssetFormValues>
            name="retirementDate"
            label={t('assets.fields.retirementDate')}
            required
            hint={t('assets.fields.retirementDateHint')}
          />
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField<AssetFormValues> name="serialNumber" label={t('assets.fields.serialNumber')} />
          <TextField<AssetFormValues> name="licenseKey" label={t('assets.fields.licenseKey')} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* A nettó ár és az áfa-kulcs KÖTELEZŐ (az Odin-oszlop NOT NULL) — a csillag ezt jelzi. */}
          <TextField<AssetFormValues>
            name="netPrice"
            label={t('assets.fields.netPrice')}
            required
            hint={t('assets.fields.netPriceHint')}
          />
          <TextField<AssetFormValues> name="vatRate" label={t('assets.fields.vatRate')} required />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DateField<AssetFormValues> name="purchaseDate" label={t('assets.fields.purchaseDate')} />
          <TextField<AssetFormValues>
            name="invoiceNumber"
            label={t('assets.fields.invoiceNumber')}
          />
        </div>
        <TextField<AssetFormValues>
          name="purchaseLocation"
          label={t('assets.fields.purchaseLocation')}
        />
        {/* Lejárat / megújítás — CSAK szoftvernél (előfizetés, licenc); erre megy a napi lejárat-értesítő. */}
        {lookup.isSoftwareType(type) && (
          <DateField<AssetFormValues>
            name="expiryDate"
            label={t('assets.fields.expiryDate')}
            hint={t('assets.fields.expiryDateHint')}
          />
        )}
        <AssetRecipientFields<AssetFormValues>
          personField="holderIdentityId"
          personLabel={t('assets.fields.holder')}
          personPlaceholder={t('assets.fields.holderPlaceholder')}
          personRequired
          initialPerson={
            asset?.holderId && asset.holderName
              ? { id: asset.holderId, label: asset.holderName }
              : null
          }
        />
        {/* A csapat nem mező: a szerver a birtokos beosztásából számolja — itt csak megjelenítjük. */}
        {asset?.teamName && (
          <Text variant="secondary" as="p" className="text-sm">
            {t('assets.fields.teamComputed', { team: asset.teamName })}
          </Text>
        )}
      </Form>
    </FormSheet>
  );
}
